import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { LabApiError, labApi, loginUrl } from './api'
import { Loading, ResultCard, SectionHead } from './components'
import './detail.css'
import { formatCost, formatScore, modelLabel, modelPath, runPath, scenePath, testerPath, vendorName } from './format'
import { SCENES, sceneApi, sceneMineApi, type SceneSlug } from './scenes'
import { useT } from './i18n'
import { useSignedIn } from './session'
import { SkillRunsSection } from './skill-links'
import type { LabResult, LabRun, LabSummary } from './types'

// ─── Shared: filtered, paginated result wall ─────────────────

type Sort = 'score' | 'flop' | 'new' | 'cheap'
const PAGE = 24

export function ResultBrowser({ model, author, showAuthor = true, scene = 'pelican-test' }: { model?: string; author?: string; showAuthor?: boolean; scene?: SceneSlug }) {
  const t = useT()
  const info = SCENES[scene]
  const units = t.scene[scene].units
  const kindOptions = info.tracksMotion ? (['', 'static', 'animated'] as const) : [] as const
  const [kind, setKind] = useState<string>('')
  const [sort, setSort] = useState<Sort>('score')
  const [items, setItems] = useState<LabResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const query = useMemo(() => {
    const p = new URLSearchParams({ sort, limit: String(PAGE) })
    if (kind) p.set('kind', kind)
    if (model) p.set('model', model)
    if (author) p.set('author', author)
    return p.toString()
  }, [kind, sort, model, author])

  const load = async (offset: number) => {
    setLoading(true)
    setFailed(false)
    try {
      const p = new URLSearchParams(query)
      p.set('offset', String(offset))
      const data = await labApi<{ items: LabResult[]; total: number }>(`${sceneApi(scene)}/results?${p}`)
      setItems((prev) => (offset === 0 ? data.items : [...prev, ...data.items]))
      setTotal(data.total)
    } catch (e) {
      if (!(e instanceof LabApiError)) throw e
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { void load(0) }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="row-between wrap">
        {kindOptions.length ? (
          <div className="segmented" role="group" aria-label={t.browse.typeAria}>
            {([['', t.browse.all], ['static', t.browse.classic], ['animated', t.browse.animated]] as const).map(([k, label]) => (
              <button key={k || 'all'} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>{label}</button>
            ))}
          </div>
        ) : null}
        <div className="row">
          <label className="sr-only" htmlFor="browse-sort">{t.browse.sort}</label>
          <select id="browse-sort" className="field field-inline" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="score">{t.browse.bestFirst}</option><option value="flop">{t.browse.wildestFirst}</option><option value="new">{t.browse.newest}</option><option value="cheap">{t.browse.cheapest}</option>
          </select>
        </div>
      </div>
      <p className="eyebrow muted explore-count" aria-live="polite">{loading && items.length === 0 ? t.browse.loadingUnits(units) : t.browse.count(total, units)}</p>
      <div className="grid-cards">{items.map((r) => <ResultCard key={r.id} result={r} showAuthor={showAuthor} scene={scene} />)}</div>
      {failed ? <p className="muted">{t.browse.couldNotLoad(units)} <button type="button" className="link-btn" onClick={() => load(0)}>{t.common.tryAgain}</button></p> : null}
      {!loading && !failed && total === 0 ? <p className="muted">{t.browse.noneHere(units)}</p> : null}
      {items.length < total ? (
        <div className="center pad-top">
          <button type="button" disabled={loading} onClick={() => load(items.length)} className="btn btn-ghost">{loading ? t.common.loading : t.browse.showMore(units)}</button>
        </div>
      ) : null}
    </div>
  )
}

// ─── Profiles: one model, one tester ─────────────────────────

interface Profile {
  by: 'model' | 'author'
  name: string
  results: number
  runs: number
  ok_rate: number
  score: number | null
  avg_cost: number | null
  motion_rate: number | null
  first_at: string | null
  last_at: string | null
  breakdown: Array<{ name: string; results: number; ok_rate: number; score: number | null; last_at: string }>
}

function useProfile(by: 'model' | 'author', name: string | null, scene: SceneSlug) {
  const [state, setState] = useState<{ data: Profile | null; error: LabApiError | null; loading: boolean }>({ data: null, error: null, loading: true })
  useEffect(() => {
    if (!name) return
    let cancelled = false
    setState({ data: null, error: null, loading: true })
    labApi<Profile>(`${sceneApi(scene)}/profile?${by}=${encodeURIComponent(name)}`)
      .then((data) => { if (!cancelled) setState({ data, error: null, loading: false }) })
      .catch((error: unknown) => { if (!cancelled) setState({ data: null, error: error instanceof LabApiError ? error : new LabApiError(0, 'Network error.'), loading: false }) })
    return () => { cancelled = true }
  }, [by, name, scene])
  return state
}

// Dates are formatted per language via t.common.day.

function ProfileStats({ p, rank, scene = 'pelican-test' }: { p: Profile; rank?: number; scene?: SceneSlug }) {
  const t = useT()
  const info = SCENES[scene]
  const unitPlural = t.scene[scene].units
  const facts: Array<[string, string]> = [
    ...(rank ? [[t.profile.rank, `#${rank}`] as [string, string]] : []),
    [t.profile.overall, p.score != null ? `${formatScore(p.score)}/5` : '—'],
    [t.scene[scene].ok, `${Math.round(p.ok_rate * 100)}%`],
    ...(info.tracksMotion ? [[t.profile.actuallyMoves, p.motion_rate == null ? '—' : `${Math.round(p.motion_rate * 100)}%`] as [string, string]] : []),
    [t.profile.avgCost, formatCost(p.avg_cost)],
    [unitPlural[0].toUpperCase() + unitPlural.slice(1), String(p.results)],
    [t.profile.runs, String(p.runs)],
  ]
  return (
    <dl className="profile-stats">
      {facts.map(([k, v]) => <div key={k}><dt className="eyebrow">{k}</dt><dd className="num">{v}</dd></div>)}
    </dl>
  )
}

function Breakdown({ p, scene = 'pelican-test' }: { p: Profile; scene?: SceneSlug }) {
  const t = useT()
  const units = t.scene[scene].units
  const isModel = p.by === 'model'
  return (
    <div className="table-wrap">
      <table className="board">
        <thead><tr>
          <th scope="col">{isModel ? t.profile.testerCol : t.profile.modelCol}</th>
          <th scope="col" className="r">{units[0].toUpperCase() + units.slice(1)}</th>
          <th scope="col" className="r">{t.scene[scene].ok}</th>
          <th scope="col" className="r">{t.profile.overall}</th>
          <th scope="col" className="r">{t.profile.lastRun}</th>
        </tr></thead>
        <tbody>
          {p.breakdown.map((row) => (
            <tr key={row.name}>
              <td>
                <Link to={isModel ? testerPath(row.name, scene) : modelPath(row.name, scene)} className="model-cell">
                  <span aria-hidden="true" className="avatar">{(isModel ? row.name : modelLabel(row.name)).charAt(0).toUpperCase()}</span>
                  <span className="strong">{isModel ? row.name : modelLabel(row.name)}</span>
                </Link>
              </td>
              <td className="r num">{row.results}</td>
              <td className="r num">{Math.round(row.ok_rate * 100)}%</td>
              <td className="r num">{formatScore(row.score)}</td>
              <td className="r num">{t.common.day(row.last_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ProfileError({ title, scene = 'pelican-test' }: { title: string; scene?: SceneSlug }) {
  const t = useT()
  return (
    <div className="measure pad-xl">
      <h1>{title}</h1>
      <p className="lede muted">{t.profile.noPublicYet(t.scene[scene].units)}</p>
      <div className="actions"><Link to={`${scenePath(scene)}#run`} className="btn btn-ink">{t.common.runScene(scene)}</Link></div>
    </div>
  )
}

export function ModelProfilePage({ summary, model, scene = 'pelican-test' }: { summary: LabSummary | null; model: string; scene?: SceneSlug }) {
  const t = useT()
  const info = SCENES[scene]
  const units = t.scene[scene].units
  const { data: p, loading } = useProfile('model', model, scene)
  const pageTitle = t.model.profileTitle(modelLabel(model), info.name)
  useEffect(() => { document.title = pageTitle }, [pageTitle])
  if (loading) return <Loading />
  if (!p) return <ProfileError title={modelLabel(model)} scene={scene} />
  const row = summary?.leaderboard.find((r) => r.model === model)
  const rank = row?.rank ?? 0
  return (
    <div className={`dx scene-${scene}`}>
      <section className="dx-hero profile-hero">
        <div className="measure">
          <Link to={`${scenePath(scene)}#leaderboard`} className="eyebrow">{t.model.backLeaderboard}</Link>
          <p className="eyebrow profile-kicker">{row?.sota ? <span className="sota-badge hero-sota">SOTA</span> : null}{vendorName(model) || t.common.model} · {t.common.publishedAt(row?.published_at)} · {t.model.firstTested(t.common.day(p.first_at))}</p>
          <h1 className="display">{modelLabel(model)}</h1>
          <p className="mono small profile-id">{model}</p>
          <ProfileStats p={p} rank={rank || undefined} scene={scene} />
        </div>
      </section>
      <section className="section">
        <div className="measure">
          <SectionHead eyebrow={units[0].toUpperCase() + units.slice(1)} title={t.model.everyFrom(t.scene[scene].unit, modelLabel(model))} lede={t.model.oneTryPerRun} />
          <ResultBrowser model={model} scene={scene} />
        </div>
      </section>
      {scene === 'pelican-test' ? <SkillRunsSection model={model} /> : null}
      <section className="section tint">
        <div className="measure">
          <SectionHead eyebrow={t.model.testersEyebrow} title={t.model.whoTested} lede={t.model.whoTestedLede} />
          <Breakdown p={p} scene={scene} />
        </div>
      </section>
    </div>
  )
}

export function TesterPage({ scene = 'pelican-test' }: { scene?: SceneSlug }) {
  const t = useT()
  const units = t.scene[scene].units
  const { name = '' } = useParams()
  const author = decodeURIComponent(name)
  const { data: p, loading } = useProfile('author', author, scene)
  const pageTitle = t.tester.title(author, units)
  useEffect(() => { document.title = pageTitle }, [pageTitle])
  if (loading) return <Loading />
  if (!p) return <ProfileError title={author} scene={scene} />
  return (
    <div className={`dx scene-${scene}`}>
      <section className="dx-hero profile-hero">
        <div className="measure">
          <Link to={`${scenePath(scene)}#testers`} className="eyebrow">{t.tester.backTesters}</Link>
          <p className="eyebrow profile-kicker">{t.tester.since(t.common.day(p.first_at))}</p>
          <h1 className="display">{author}</h1>
          <ProfileStats p={p} scene={scene} />
        </div>
      </section>
      <section className="section">
        <div className="measure">
          <SectionHead eyebrow={units[0].toUpperCase() + units.slice(1)} title={t.tester.everythingRan(author)} lede={t.tester.everythingLede} />
          <ResultBrowser author={author} showAuthor={false} scene={scene} />
        </div>
      </section>
      <section className="section tint">
        <div className="measure">
          <SectionHead eyebrow={t.tester.modelsEyebrow} title={t.tester.modelsTested} lede={t.tester.modelsLede} />
          <Breakdown p={p} scene={scene} />
        </div>
      </section>
    </div>
  )
}

// ─── Browse hub on the landing page ──────────────────────────

export function BrowseHub({ summary, scene = 'pelican-test' }: { summary: LabSummary; scene?: SceneSlug }) {
  const t = useT()
  const sc = t.scene[scene]
  const [tab, setTab] = useState<'model' | 'author'>('model')
  const models = summary.leaderboard
  const authors = summary.authors
  return (
    <div>
      <div className="segmented" role="tablist" aria-label={t.browse.byAria}>
        <button type="button" role="tab" aria-selected={tab === 'model'} aria-pressed={tab === 'model'} onClick={() => setTab('model')}>{t.browse.byModel(models.length)}</button>
        <button type="button" role="tab" aria-selected={tab === 'author'} aria-pressed={tab === 'author'} onClick={() => setTab('author')}>{t.browse.byTester(authors.length)}</button>
      </div>
      <div className="browse-grid" role="tabpanel">
        {tab === 'model'
          ? models.map((m) => (
            <Link key={m.model} to={modelPath(m.model, scene)} className="browse-tile">
              <span className="eyebrow subtle">{vendorName(m.model) || t.common.model}{m.sota ? <span className="sota-badge">SOTA</span> : null}</span>
              <span className="browse-name">{modelLabel(m.model)}</span>
              <span className="browse-meta">{t.common.publishedAt(m.published_at)} · <b className="num">{formatScore(m.score)}</b>/5 · {m.results} {m.results === 1 ? sc.unit : sc.units} · {t.common.testers(m.testers)}</span>
            </Link>
          ))
          : authors.map((a) => (
            <Link key={a.name} to={testerPath(a.name, scene)} className="browse-tile">
              <span className="eyebrow subtle">{t.common.tester}</span>
              <span className="browse-name">{a.name}</span>
              <span className="browse-meta num">{a.count} {a.count === 1 ? sc.unit : sc.units}</span>
            </Link>
          ))}
      </div>
    </div>
  )
}

// ─── My runs ─────────────────────────────────────────────────

// Status labels live in i18n (t.myRuns.status).

export function MyRunsPage({ scene = 'pelican-test' }: { scene?: SceneSlug }) {
  const signedIn = useSignedIn()
  const t = useT()
  const info = SCENES[scene]
  const [runs, setRuns] = useState<LabRun[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pageTitle = t.myRuns.title
  const loadError = t.myRuns.loadError
  useEffect(() => { document.title = pageTitle }, [pageTitle])
  useEffect(() => {
    if (!signedIn) return
    labApi<{ items: LabRun[] }>(sceneMineApi(scene), { auth: true })
      .then((d) => setRuns(d.items))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : loadError))
  }, [signedIn, scene]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`dx scene-${scene}`}>
      <section className="dx-hero">
        <div className="measure dx-hero-pad">
          <p className="eyebrow dx-kicker">{info.name}</p>
          <h1>{t.myRuns.heading}</h1>
          <p className="lede">{t.myRuns.lede(info.name)}</p>
        </div>
      </section>
      <section className="section">
        <div className="measure">
          {!signedIn ? (
            <div className="panel signin-panel">
              <div><p className="card-title">{t.myRuns.signInTitle}</p></div>
              <a href={loginUrl(`${scenePath(scene)}/me`)} className="btn btn-ink">{t.myRuns.signIn}</a>
            </div>
          ) : error ? <p className="error">{error}</p>
            : runs === null ? <p className="muted">{t.myRuns.loading}</p>
            : runs.length === 0 ? (
              <div className="panel signin-panel">
                <div><p className="card-title">{t.myRuns.noneTitle}</p><p className="muted">{t.myRuns.noneLede(t.scene[scene].units)}</p></div>
                <Link to={`${scenePath(scene)}#run`} className="btn btn-ink">{t.common.runScene(scene)}</Link>
              </div>
            ) : (
              <div className="run-list">
                {runs.map((run) => {
                  const done = run.results.filter((r) => r.ok)
                  const best = [...done].sort((a, b) => (b.score ?? -1) - (a.score ?? -1))[0]
                  return (
                    <Link key={run.id} to={runPath(run.id, scene)} className="run-row">
                      <span className="run-thumbs" aria-hidden="true">
                        {done.slice(0, 4).map((r) => (r.png ? <img key={r.id} src={r.png} alt="" loading="lazy" /> : null))}
                      </span>
                      <span className="stack-sm">
                        <span className="strong">{run.title}</span>
                        <span className="small muted">{t.myRuns.byline(t.common.dateTime(run.created_at), run.models.length, run.author)}</span>
                      </span>
                      <span className="run-row-meta">
                        <span className={`pill ${run.status === 'failed' ? 'pill-error' : ''}`}>{t.myRuns.status[run.status] ?? run.status}</span>
                        <span className="small muted num">{best ? t.myRuns.best(modelLabel(best.model), formatScore(best.score)) : '—'} · {formatCost(run.total_cost)}</span>
                      </span>
                    </Link>
                  )
                })}
              </div>
            )}
        </div>
      </section>
    </div>
  )
}
